import type { SectionProps } from "../context";
import { DAY_LABELS, groupedHours, hoursLabel, sortedHours } from "../helpers";
import { Icon } from "../icons";
import { Container, EditorHint, MultiLine, STACK, SectionHeader, SectionShell, cx } from "../ui";

export function OpeningHours({ node, ctx }: SectionProps<"opening-hours">) {
  const c = node.content;
  const hours = sortedHours(ctx.site.hours);
  if (hours.length === 0 && !ctx.editor) return null;
  const note = c.note ? <MultiLine ctx={ctx} text={c.note} path="content.note" className="mt-4 text-sm text-site-muted" /> : null;
  const empty = <EditorHint ctx={ctx}>Jam buka belum diisi. Atur di Info bisnis → Jam buka.</EditorHint>;

  if (node.variant === "card") {
    const groups = groupedHours(ctx.site.hours);
    return (
      <SectionShell ctx={ctx} node={node}>
        <Container size="narrow">
          <div className="baze-reveal-pop mx-auto max-w-lg rounded-site-lg border border-site-border bg-site-surface p-7 text-center shadow-site-md @3xl:p-10">
            <span aria-hidden="true" className="mx-auto mb-5 grid size-12 place-items-center rounded-full bg-site-primary/10 text-site-primary">
              <Icon name="clock" className="size-6" />
            </span>
            <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} align="center" />
            {groups.length > 0 ? (
              <ul className="mt-8 space-y-3 text-left">
                {groups.map((g) => (
                  <li key={g.label} className="flex items-baseline justify-between gap-4 border-b border-dashed border-site-border pb-3 last:border-0 last:pb-0">
                    <span className="font-medium text-site-text">{g.label}</span>
                    <span className={cx("tabular-nums", g.closed ? "text-site-muted" : "font-semibold text-site-text")}>{g.value}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="mt-6">{empty}</div>
            )}
            {note}
          </div>
        </Container>
      </SectionShell>
    );
  }

  return (
    <SectionShell ctx={ctx} node={node}>
      <Container>
        <div className="grid gap-10 @4xl:grid-cols-[1fr_1.2fr] @4xl:items-start @4xl:gap-16">
          <div>
            <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} />
            {note}
          </div>
          {hours.length > 0 ? (
            <div className="overflow-hidden rounded-site-md border border-site-border bg-site-surface shadow-site-sm @4xl:mt-0">
              <table className="w-full text-left">
                <caption className="sr-only">{c.title}</caption>
                <thead className="sr-only">
                  <tr>
                    <th scope="col">Hari</th>
                    <th scope="col">Jam</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-site-border">
                  {hours.map((h) => (
                    <tr key={h.day}>
                      <th scope="row" className="px-5 py-4 font-medium text-site-text @3xl:px-7">
                        {DAY_LABELS[h.day]}
                      </th>
                      <td className={cx("px-5 py-4 text-right tabular-nums @3xl:px-7", h.closed ? "text-site-muted" : "font-semibold text-site-text")}>{hoursLabel(h)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={cx(STACK, "baze-reveal @4xl:mt-0")}>
              {empty}
            </div>
          )}
        </div>
      </Container>
    </SectionShell>
  );
}
